import {
  AddressApiResponseAddress,
  ValidationItems,
  ValidationProps,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';
import { StringIndexedObject } from '@kanda-libs/ks-design-library';
import {
  Dispatch,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FunctionComponent,
  type ReactNode,
} from 'react';
import {
  getValidationConditions,
  getValidationErrors,
  validateAddressSelect,
} from 'utils';
import { AddressFieldsLabels } from '.';
import { getWidgetPrefix } from './helpers';

export interface AddressFieldsProviderProps {
  children: ReactNode;
  baseFieldName: string;
  showYears?: boolean;
  showLabels?: boolean;
  labels?: AddressFieldsLabels;
}

export interface AddressFieldsContextType {
  baseFieldName: string;
  rootName: string;
  postCode: string;
  widgetPrefix: string;
  apiPostcode: string;
  postcodeCallback: (data: StringIndexedObject) => unknown;
  postcodeName: string | undefined;
  setPostcodeName: (id: string) => void;
  addresses: AddressApiResponseAddress[] | null;
  manual: boolean;
  enterManually: () => void;
  selectProps: ValidationProps;
  buildingValidation: ValidationItems;
  postcodeIsValidating: boolean;
  setPostcodeIsValidating: Dispatch<SetStateAction<boolean>>;
  showYears: boolean;
  showLabels: boolean;
  labels?: AddressFieldsLabels;
  postcodeValidation: ValidationItems;
  onPostcodeBlur: () => void;
  onBuildingBlur: () => void;
}

export const AddressFieldsContext =
  createContext<AddressFieldsContextType | null>(null);

export const useAddressFieldsContext = () =>
  useContext(AddressFieldsContext) as AddressFieldsContextType;

const AddressFieldsProvider: FunctionComponent<AddressFieldsProviderProps> =
  function ({
    children,
    baseFieldName,
    showYears = true,
    showLabels = true,
    labels,
  }) {
    const [addresses, setAddresses] = useState<
      AddressApiResponseAddress[] | null
    >(null);
    const [manual, setManual] = useState<boolean>(true);
    const [apiPostcode, setApiPostcode] = useState<string>('');
    const [postcodeIsValidating, setPostcodeIsValidating] =
      useState<boolean>(false);

    const { getValues, setValue, trigger } = useFormContext();

    const postcodeName = useMemo(
      () => `${baseFieldName}.postcode`,
      [baseFieldName],
    );

    const postcodeValidation = useMemo(
      (): ValidationItems => ({
        required: { value: true, message: 'Postcode is required.' },
        pattern: {
          value:
            /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/,
          message: 'Postcode input is invalid',
        },
      }),
      [],
    );

    const onPostcodeBlur = useCallback(() => {
      const postcode = getValues(postcodeName) as string | undefined;
      if (!postcode) return;
      const clean = postcode.toUpperCase().replace(/\s+/g, '');
      const postcodeRegex =
        /^([A-Za-z][A-Ha-hJ-Yj-y]?[0-9][A-Za-z0-9]? ?[0-9][A-Za-z]{2}|[Gg][Ii][Rr] ?0[Aa]{2})$/;
      if (!postcodeRegex.test(clean)) {
        return;
      }
      const formatted = [clean.slice(0, -3), clean.slice(-3)].join(' ');
      setValue(postcodeName, formatted);
    }, [getValues, setValue, postcodeName]);

    const onBuildingBlur = useCallback(() => {
      trigger(`${baseFieldName}.building_name`);
      trigger(`${baseFieldName}.building_number`);
    }, [trigger, baseFieldName]);

    const postcodeCallback = useCallback((results: StringIndexedObject) => {
      setApiPostcode(results?.postcode);
      setAddresses(results?.addresses);
    }, []);
    const enterManually = useCallback(() => setManual(true), []);

    const rootName = useMemo(() => {
      if (!postcodeName) return baseFieldName;
      return postcodeName.replace('.postcode', '');
    }, [postcodeName, baseFieldName]);

    const widgetPrefix = useMemo(() => {
      if (!rootName) return getWidgetPrefix(baseFieldName);
      return getWidgetPrefix(rootName);
    }, [baseFieldName, rootName]);

    const [buildingName, buildingNumber, postCode, line1, selected] = useWatch({
      name: [
        `${rootName}.building_name`,
        `${rootName}.building_number`,
        `${rootName}.postcode`,
        `${rootName}.line_1`,
        `${rootName}.selected`,
      ],
    });

    const selectValidation = useMemo(
      () => ({
        validate: {
          value: () => validateAddressSelect(getValues(), rootName),
          message:
            'You must select an address or enter the address details manually',
        },
      }),
      [getValues, rootName],
    );

    const selectProps = useMemo(
      () => ({
        validationConditions: getValidationConditions(selectValidation),
        validationErrors: getValidationErrors(selectValidation),
        placeholder: addresses === null ? 'Enter postcode above' : undefined,
      }),
      [selectValidation, addresses],
    );

    const buildingValidation = useMemo(
      () => ({
        required: {
          value: !buildingName && !buildingNumber,
          message: 'Building name or number is required',
        },
      }),
      [buildingName, buildingNumber],
    );

    const values = useMemo(
      () => ({
        baseFieldName,
        rootName,
        widgetPrefix,
        postcodeCallback,
        postcodeName,
        setPostcodeName: () => {},
        postCode,
        apiPostcode,
        addresses,
        manual,
        enterManually,
        selectProps,
        buildingValidation,
        postcodeIsValidating,
        setPostcodeIsValidating,
        showYears,
        showLabels,
        labels,
        postcodeValidation,
        onPostcodeBlur,
        onBuildingBlur,
      }),
      [
        baseFieldName,
        rootName,
        widgetPrefix,
        postcodeCallback,
        postcodeName,
        postCode,
        apiPostcode,
        addresses,
        manual,
        enterManually,
        selectProps,
        buildingValidation,
        postcodeIsValidating,
        showYears,
        showLabels,
        labels,
        postcodeValidation,
        onPostcodeBlur,
        onBuildingBlur,
      ],
    );

    useEffect(() => {
      if (!line1 || !addresses || addresses?.length === 0) return;
      const match = addresses.findIndex((address) => address.line_1 === line1);
      if (match === selected) return;
      setValue(`${rootName}.selected`, match);
    }, [line1, addresses, selected, setValue, rootName]);

    return (
      <AddressFieldsContext.Provider value={values}>
        {children}
      </AddressFieldsContext.Provider>
    );
  };

export default AddressFieldsProvider;
